import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { useToast } from '@codes/ui-kit/components/ui/use-toast';
import { Check, Copy } from 'lucide-react';

const COPY_RESET_DELAY = 2000;

interface CopyButtonProps {
    content: string;
    copyMessage?: string;
}

export const CopyButton = React.memo(({ content, copyMessage }: CopyButtonProps) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();
    const timeoutRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set new timeout with cleanup
            timeoutRef.current = setTimeout(() => {
                setCopied(false);
                timeoutRef.current = undefined;
            }, COPY_RESET_DELAY);
        } catch (err) {
            console.error('Failed to copy:', err);
            toast({
                description: t('agents.copyButton.error', '복사에 실패했습니다'),
                variant: 'destructive',
                duration: COPY_RESET_DELAY,
            });
        }
    };

    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={handleCopy}
            className="h-6 w-6 rounded-md transition-colors"
            title={copied ? t('agents.copyButton.copied', '복사됨!') : t('agents.copyButton.title', '메시지 복사')}
        >
            {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
        </Button>
    );
});

CopyButton.displayName = 'CopyButton';
